import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { Form, Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';

import Modal from 'components/Modal';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { CloseIcon, Icon } from 'utils/icons';
import DropDownMenu from 'components/DropDownMenu';
import Button from 'components/Button';
import { debounce, stopPropagation, toastStyle } from 'utils/helper';
import { useAppContext } from 'contexts/AppContext';
import { validateEmail } from 'utils/validator';
import { useAccountSettingQueries, useBudgetQueries } from 'utils/api/queries';
import { buildPrivateBudgetShareUrl } from 'utils/budgetDeepLinks';

import CollaboratorsList from './CollaboratorsList';

import './styles.scss';

const ShareBudget = () => {
  const {
    shareBudgetMutation,
    updatePublicBudgetLinkMutation,
    showShareBudget,
    hideShareBudgetModal,
    budgetData,
    refetchCollaborators,
    removeBudgetAccessMutation,
    isBudgetOwner,
    currentBudgetViewState,
  } = useHomeScreenContext();
  const { permissions, userData } = useAppContext();
  const dropdownMenuRef = useRef();
  const itemRefs = useRef([]);
  const inputRef = useRef();
  const [inputValue, setInputValue] = useState('');
  const [selectedEmails, setSelectedEmail] = useState([]);
  const [openMenu, setOpenMenu] = useState(false);
  const [userList, setUserList] = useState([]);
  const [selectedPermission, setSelectedPermission] = useState(
    budgetData?.my_permission === 'viewer'
      ? permissions?.filter(item => item.label === 'viewer')?.[0]?.value
      : permissions?.[0]?.value
  );
  const [searchParam, setSearchParam] = useState('');
  const [copyUrl, setCopyUrl] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showPublicLinkConfirm, setShowPublicLinkConfirm] = useState(false);
  const [linkToCurrentLocation, setLinkToCurrentLocation] = useState(true);
  const canManagePublicLink = isBudgetOwner?.(budgetData);

  const {
    data: publicBudgetLinkResponse,
    isFetching: isFetchingPublicBudgetLink,
    refetch: refetchPublicBudgetLink,
  } = useBudgetQueries.usePublicBudgetLinkSettingsQuery({
    budgetId: budgetData?.id,
    enabled: Boolean(showShareBudget && budgetData?.id && canManagePublicLink),
  });

  const publicBudgetLinkSettings = publicBudgetLinkResponse?.data;
  const publicLinkEnabled = Boolean(publicBudgetLinkSettings?.enabled);
  const publicUrl = publicBudgetLinkSettings?.public_path
    ? `${window.location.origin}${publicBudgetLinkSettings.public_path}`
    : publicBudgetLinkSettings?.public_url;
  const embedUrl = publicBudgetLinkSettings?.embed_path
    ? `${window.location.origin}${publicBudgetLinkSettings.embed_path}`
    : publicBudgetLinkSettings?.embed_url;
  const embedCode = embedUrl
    ? `<iframe src="${embedUrl}" title="${
        budgetData?.name || 'CineBudget Pro public budget'
      }" width="100%" height="640" style="border:0;"></iframe>`
    : '';

  const { data: userSearchData } = useAccountSettingQueries.useSearchUserQuery({
    searchParam,
    enabled: Boolean(searchParam),
  });

  const updateSearchParam = useMemo(
    () => debounce(value => setSearchParam(value), 500),
    []
  );

  useEffect(() => {
    if (inputValue) {
      const searchValue = inputValue?.toLowerCase();
      const userOptions =
        userSearchData?.data?.filter(
          item =>
            (item?.name?.toLowerCase()?.includes(searchValue) ||
              item?.email?.toLowerCase()?.includes(searchValue)) &&
            !selectedEmails.find(
              selected =>
                selected?.value?.toLowerCase() === item?.email?.toLowerCase()
            ) &&
            item?.email !== userData?.email
        ) || [];
      setUserList(userOptions);
    }
  }, [userSearchData, selectedEmails, inputValue, userData]);

  useEffect(() => {
    inputValue?.length >= 3
      ? updateSearchParam(inputValue)
      : setSearchParam('');
  }, [inputValue, updateSearchParam]);

  useEffect(() => {
    setOpenMenu(Boolean(userList.length));
  }, [userList]);

  const handleChangePermission = selectedItem => {
    setSelectedPermission(selectedItem);
  };

  const handleRemoveBudgetAccess = userInfo => {
    removeBudgetAccessMutation.mutate(
      { id: userInfo?.id },
      {
        onSuccess: () => {
          toast.success(
            `Access has been removed for the user ${
              userInfo?.name || userInfo?.email
            }.`,
            toastStyle
          );
          refetchCollaborators?.();
        },
        onError: error => {
          const message =
            error?.response?.data?.message?.non_field_error?.[0] ||
            error?.response?.data?.non_field_errors?.[0];
          toast.error(
            message ||
              `An error occurred while revoking user access. Please try again later`,
            toastStyle
          );
        },
      }
    );
  };

  const handleShareBudget = async (action, userInfo) => {
    if (action === 'remove_access') {
      return handleRemoveBudgetAccess(userInfo);
    }

    if (!(selectedEmails?.length || userInfo?.email)) {
      return false;
    }

    const payload = userInfo?.email
      ? {
          budget: budgetData?.id,
          permissions: userInfo.permissions,
          collaborator_emails: [userInfo?.email],
        }
      : {
          budget: budgetData?.id,
          permissions: [selectedPermission],
          collaborator_emails: selectedEmails?.map(item => item.value),
        };

    try {
      await shareBudgetMutation.mutateAsync(payload);
      const message =
        action === 'resend'
          ? `Resent invite email to '${userInfo?.email}'.`
          : `User '${
              userInfo?.name || userInfo?.email
            }' has been granted with ${permissionLabel(
              userInfo?.permissions?.[0]
            )} access for the budget "${budgetData?.name}".`;
      toast.success(
        userInfo?.email
          ? message
          : `Budget "${budgetData?.name}" has been successfully shared with selected users.`,
        toastStyle
      );
      if (!userInfo?.email) {
        setSelectedEmail([]);
      }
      refetchCollaborators?.();
    } catch (error) {
      const message =
        error?.response?.data?.message?.non_field_error?.[0] ||
        error?.response?.data?.non_field_errors?.[0];
      toast.error(
        message ||
          `An error occurred while sharing the budget. Please try again later`,
        toastStyle
      );
    }
  };

  const handleCancel = () => {
    hideShareBudgetModal();
  };

  const setSelectedItem = useCallback(item => {
    const rawValue = item?.value || inputRef.current?.value || '';
    const value = rawValue.trim().toLowerCase();
    if (!value || !validateEmail(value)) return;

    setSelectedEmail(prev =>
      prev.some(tag => tag?.value?.trim().toLowerCase() === value)
        ? prev
        : [
            ...prev,
            {
              value,
              label: item?.label || rawValue.trim(),
            },
          ]
    );
    setInputValue('');
    setOpenMenu(false);
  }, []);

  const handleInputChange = event => {
    const newValue = event?.target?.value;
    if (/[\s;,:]/.test(newValue)) {
      const emails = newValue.split(/[\s;,:]/);
      const completeEmails = emails.filter(email =>
        validateEmail(email?.toLowerCase()?.trim())
      );
      completeEmails.forEach(email => {
        setSelectedItem({ value: email?.trim(), label: email?.trim() });
      });
      setInputValue(emails[emails.length - 1] || '');
    } else {
      setInputValue(newValue);
    }
  };

  const handleKeyDown = event => {
    switch (event.key) {
      case 'ArrowDown':
        setHighlightedIndex(prevIndex => (prevIndex + 1) % userList.length);
        setOpenMenu(true);
        event.preventDefault();
        break;
      case 'ArrowUp':
        setHighlightedIndex(prevIndex =>
          prevIndex === 0 ? userList.length - 1 : prevIndex - 1
        );
        setOpenMenu(true);
        event.preventDefault();
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && highlightedIndex <= userList.length) {
          setSelectedItem({
            value: userList[highlightedIndex]?.email,
            label:
              userList[highlightedIndex]?.name ||
              userList[highlightedIndex]?.email,
          });
        }
        event.preventDefault();
        break;
      case 'Tab':
        setOpenMenu(false);
        setSelectedItem();
        event.preventDefault();
        break;
      case 'Escape':
        setOpenMenu(false);
        setInputValue('');
        event.preventDefault();
        break;
      default:
        return '';
    }
  };

  const handleOnChange = userId => {
    const item = userList?.find(
      user => user?.id?.toString() === userId?.toString()
    );
    setSelectedItem({ label: item?.name || item?.email, value: item?.email });
  };

  const handleBlur = () => {
    setSelectedItem();
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(copyUrl);
    toast.success('Private budget link copied to clipboard.', toastStyle);
  };

  const handleCopyPublicValue = (value, successMessage) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(successMessage, toastStyle);
  };

  const handleUpdatePublicLink = async enabled => {
    try {
      await updatePublicBudgetLinkMutation.mutateAsync({
        budgetId: budgetData?.id,
        enabled,
      });
      await refetchPublicBudgetLink?.();
      toast.success(
        enabled ? 'Public link enabled.' : 'Public link disabled.',
        toastStyle
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Could not update the public budget link. Please try again.',
        toastStyle
      );
    }
  };

  const handlePublicLinkToggle = event => {
    const enabled = event.target.checked;
    if (enabled && !publicLinkEnabled) {
      setShowPublicLinkConfirm(true);
      return;
    }

    handleUpdatePublicLink(false);
  };

  const handleConfirmPublicLink = async () => {
    await handleUpdatePublicLink(true);
    setShowPublicLinkConfirm(false);
  };

  const handleClickOutside = event => {
    if (
      dropdownMenuRef.current &&
      !dropdownMenuRef.current.contains(event.target)
    ) {
      setOpenMenu(false);
    }
  };

  useEffect(() => {
    setCopyUrl(
      buildPrivateBudgetShareUrl({
        appBaseUrl: window.location.origin,
        budgetId: budgetData?.id,
        viewState: linkToCurrentLocation ? currentBudgetViewState : null,
      })
    );
  }, [budgetData?.id, currentBudgetViewState, linkToCurrentLocation]);
  useEffect(() => {
    if (highlightedIndex >= 0 && highlightedIndex < userList.length) {
      const item = itemRefs.current[highlightedIndex];
      const dropdownMenu = dropdownMenuRef.current;

      if (item && dropdownMenu) {
        const itemTop = item.offsetTop;
        const itemBottom = itemTop + item.clientHeight;
        const menuScrollTop = dropdownMenu.scrollTop;
        const menuHeight = dropdownMenu.clientHeight;

        if (itemBottom > menuScrollTop + menuHeight) {
          dropdownMenu.scrollTop = itemBottom - menuHeight;
        } else if (itemTop < menuScrollTop) {
          dropdownMenu.scrollTop = itemTop;
        }
      }
    }
  }, [highlightedIndex, userList.length]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const modalTitle = () => {
    return (
      <>
        <div className="d-flex">
          <div className="share-budget__title">Share Budget</div>
        </div>
        <div className="share-budget__sub-title">{budgetData?.name}</div>
      </>
    );
  };

  const handleDeleteEmail = email => e => {
    stopPropagation(e);
    setSelectedEmail(
      selectedEmails?.filter(selected => email?.value !== selected?.value) || []
    );
  };

  const permissionLabel = selected => {
    return permissions?.find(item => selected?.toString() === item?.value)
      ?.label;
  };

  const setInputFocus = () => {
    inputRef?.current?.focus();
  };

  const myPermission =
    budgetData?.my_permission === 'viewer'
      ? permissions.filter(data => data.label === 'viewer')
      : permissions;

  return (
    <>
      <Modal
        onHide={handleCancel}
        show={showShareBudget}
        size="xl"
        className="share-budget"
        bodyClass="share-budget__body"
        centered={true}
        title={modalTitle()}
      >
        <div className="share-budget__layout">
          <section
            className="share-budget__section share-budget__link-section"
            aria-labelledby="share-by-link-title"
          >
            <div className="share-budget__section-heading">
              <div className="share-budget__section-icon" aria-hidden="true">
                <Icon name="UserLockIcon" />
              </div>
              <div>
                <h3 id="share-by-link-title">Share by link</h3>
                <p>
                  Copy a link for invited people, or publish a view-only link.
                </p>
              </div>
            </div>

            <div className="share-budget__link-group">
              <div className="share-budget__link-label-row">
                <label htmlFor="private-budget-url">Private link</label>
                <span>Invited people only</span>
              </div>
              <p className="share-budget__field-help">
                Anyone opening this link must already have access to the budget.
              </p>
              <div className="share-budget-copy-control">
                <input
                  id="private-budget-url"
                  type="text"
                  value={copyUrl}
                  readOnly
                />
                <button
                  type="button"
                  onClick={handleCopyUrl}
                  aria-label="Copy private budget link"
                >
                  <Icon name="CopyLink" />
                  <span>Copy</span>
                </button>
              </div>
              <Form.Check
                type="checkbox"
                id="share-budget-current-location"
                className="share-budget-current-location"
                checked={linkToCurrentLocation}
                onChange={event =>
                  setLinkToCurrentLocation(event.target.checked)
                }
                label="Open the currently selected budget location"
              />
            </div>

            {canManagePublicLink && (
              <div className="share-budget-public-link">
                <div className="share-budget-public-link-header">
                  <div>
                    <div className="share-budget-section-title">
                      Public view-only link
                    </div>
                    <div className="share-budget-section-copy">
                      Anyone with this link can view the latest budget without
                      signing in. They cannot edit or comment.
                    </div>
                  </div>
                  <Form.Check
                    type="switch"
                    id="public-budget-link-toggle"
                    className="share-budget-public-link-toggle"
                    checked={publicLinkEnabled}
                    disabled={
                      isFetchingPublicBudgetLink ||
                      updatePublicBudgetLinkMutation.isLoading
                    }
                    onChange={handlePublicLinkToggle}
                    label={publicLinkEnabled ? 'On' : 'Off'}
                  />
                </div>
                {publicLinkEnabled && (
                  <div className="share-budget-public-link-fields">
                    <div className="share-budget-copy-row">
                      <label htmlFor="public-budget-url">Public link</label>
                      <div className="share-budget-copy-control">
                        <input
                          id="public-budget-url"
                          type="text"
                          value={publicUrl || ''}
                          readOnly
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleCopyPublicValue(
                              publicUrl,
                              'Public budget link copied to clipboard.'
                            )
                          }
                          aria-label="Copy public budget link"
                        >
                          <Icon name="CopyLink" />
                          <span>Copy</span>
                        </button>
                      </div>
                    </div>
                    <div className="share-budget-copy-row">
                      <label htmlFor="public-budget-embed-code">
                        Website embed code
                      </label>
                      <div className="share-budget-copy-control">
                        <input
                          id="public-budget-embed-code"
                          type="text"
                          value={embedCode}
                          readOnly
                        />
                        <button
                          type="button"
                          onClick={() =>
                            handleCopyPublicValue(
                              embedCode,
                              'Embed code copied to clipboard.'
                            )
                          }
                          aria-label="Copy public budget embed code"
                        >
                          <Icon name="CopyLink" />
                          <span>Copy</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          <section
            className="share-budget__section share-budget__people-section"
            aria-labelledby="share-with-people-title"
          >
            <div className="share-budget__section-heading">
              <div className="share-budget__section-icon" aria-hidden="true">
                <Icon name="GroupsIcon" />
              </div>
              <div>
                <h3 id="share-with-people-title">Share with people</h3>
                <p>Invite people by email and choose what they can do.</p>
              </div>
            </div>

            <div className="share-budget__invite-area">
              <label className="email-list-label" htmlFor="share-budget-email">
                Names or email addresses
              </label>
              <div className="share-budget-container">
                <div className="email-list-container" onClick={setInputFocus}>
                  <div className="tags-container">
                    {selectedEmails?.map(email => (
                      <div className="tag" key={email?.value}>
                        {email?.label}
                        <CloseIcon
                          className="remove-icon"
                          onClick={handleDeleteEmail(email)}
                        />
                      </div>
                    ))}
                    <Dropdown
                      onSelect={handleOnChange}
                      className="email-list-dropdown"
                      show={openMenu}
                    >
                      <Form.Control
                        id="share-budget-email"
                        type="text"
                        placeholder={
                          selectedEmails?.length ? '' : 'Name or email address'
                        }
                        onKeyDown={handleKeyDown}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        className="tags-input"
                        autoComplete="off"
                        value={inputValue}
                        ref={inputRef}
                      />
                      <Dropdown.Menu
                        className="email-list-dropdown-menu"
                        ref={dropdownMenuRef}
                      >
                        {userList?.map((item, index) => (
                          <Dropdown.Item
                            key={item?.id}
                            eventKey={item?.id}
                            active={index === highlightedIndex}
                            ref={el => (itemRefs.current[index] = el)}
                          >
                            {item.name || item?.email}&nbsp;
                            {item?.name && `(${item?.email})`}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div className="permissions">
                    <DropDownMenu
                      className="budget-permissions"
                      menuOptions={myPermission}
                      toggleLabel={permissionLabel(selectedPermission)}
                      selectedOption={selectedPermission}
                      onChange={handleChangePermission}
                    />
                  </div>
                </div>
                <Button
                  label={
                    <span className="share-budget__invite-button-label">
                      <Icon name="InviteIcon" />
                      Invite
                    </span>
                  }
                  className="share-budget-btn"
                  type="submit"
                  isDisabled={!selectedEmails?.length}
                  onClick={handleShareBudget}
                />
              </div>
            </div>

            <div className="share-budget__access-heading">
              <h4>People with access</h4>
              <span>Manage roles or remove access</span>
            </div>
            <CollaboratorsList
              handleShareBudget={handleShareBudget}
              permissionLabel={permissionLabel}
            />
          </section>
        </div>
      </Modal>
      <Modal
        onHide={() => setShowPublicLinkConfirm(false)}
        show={showPublicLinkConfirm}
        size="sm"
        className="share-budget public-link-confirm"
        bodyClass="share-budget__body public-link-confirm__body"
        centered={true}
        title="Enable Public Link?"
      >
        <div className="public-link-confirm__copy">
          Anyone with this link will be able to view the latest budget content.
          They will not be able to edit, change, or comment on the budget.
        </div>
        <div className="public-link-confirm__actions">
          <Button
            label="Cancel"
            variant="grey-outlined"
            onClick={() => setShowPublicLinkConfirm(false)}
            isDisabled={updatePublicBudgetLinkMutation.isLoading}
          />
          <Button
            label="Enable Link"
            onClick={handleConfirmPublicLink}
            isDisabled={updatePublicBudgetLinkMutation.isLoading}
          />
        </div>
      </Modal>
    </>
  );
};

export default ShareBudget;
